import { zodResolver } from '@hookform/resolvers/zod';
import React, { useMemo, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';

import { Form, InputField, TextareaField, MultiSelectField } from '@epoch-ui/components/Form';
import { getPendingScheduledPublishAt } from '@event-editor/utils';
import {
  TimeOffsetRelation,
  TimeOffsetUnit,
  REMINDERS_GUEST_LIST,
  normalizeTimeOffsetUnit
} from 'constants/enums';
import { queryClient } from 'api';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import {
  slackRemindersApi,
  UpsertSlackReminderInput,
  SlackReminder,
  DeleteSlackReminderInput
} from '@event-editor/api/reminder/slackReminder';
import {
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from '@epoch-ui/components/Sheet/sheet';
import { Icon } from '@epoch-ui/components/Icon';
import { Button } from '@epoch-ui/components/Button';
import { ToasterVariant, useToaster } from 'hooks/useToaster';
import { guessTimezone } from 'utils/dateTimeUtils';
import { convertSendAtToFormValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import { getDefaultPostEventReminderSendAtFormValue } from '@event-editor/tabs/EditEventCommunications/utils/postEventReminderWindow';

import { ReminderScheduler } from '../ReminderScheduler';

import {
  SlackReminderFormInput,
  SlackReminderFormValues,
  slackReminderFormSchema,
  guestStatusOptions,
  GUEST_STATUS_MAPPING
} from './reminder.schema';
import { SlackTipsModal } from './SlackTipsModal';
/**
 * @param onClose - Callback to handle closing the form
 * @param onDirtyChange - Callback to notify parent of form dirty state changes
 */
interface SlackReminderFormProps {
  onSuccess?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onBack?: () => void;
  slackReminder?: SlackReminder;
}

export const SlackReminderForm: React.FC<SlackReminderFormProps> = ({
  onSuccess,
  onDirtyChange,
  slackReminder,
  onBack
}) => {
  const event = useEditableEvent();
  const { showToast } = useToaster();
  const isCreate = !slackReminder;
  const [showSlackTipsModal, setShowSlackTipsModal] = useState(false);

  const mappedGuestStatuses = useMemo(() => {
    if (isCreate) return [];

    return (slackReminder?.selectedGuestStatuses ?? []).map((statusNumber: number) => {
      const statusKey = Object.keys(REMINDERS_GUEST_LIST).find(
        (key) => REMINDERS_GUEST_LIST[key as keyof typeof REMINDERS_GUEST_LIST] === statusNumber
      );
      const guestStatusMapping =
        GUEST_STATUS_MAPPING[statusKey as keyof typeof GUEST_STATUS_MAPPING];
      return guestStatusMapping;
    });
  }, [slackReminder, isCreate]);

  const pendingScheduledPublishAt = getPendingScheduledPublishAt(event.publishSchedule);
  const eventTimezone = event.timezoneCode ?? guessTimezone();
  const defaultPostEventSendAt = useMemo(
    () =>
      event.hasEnded
        ? getDefaultPostEventReminderSendAtFormValue({
            datetimeEnd: event.datetimeEnd,
            timezoneCode: eventTimezone
          })
        : undefined,
    [event.datetimeEnd, event.hasEnded, eventTimezone]
  );

  const form = useForm<SlackReminderFormInput, unknown, SlackReminderFormValues>({
    resolver: zodResolver(
      slackReminderFormSchema(
        event.datetimeScheduled ?? '',
        event.datetimeEnd ?? '',
        eventTimezone,
        pendingScheduledPublishAt
      )
    ),
    values: {
      title: slackReminder?.title ?? '',
      message: slackReminder?.message ?? '',
      selectedGuestStatuses: mappedGuestStatuses,
      schedule: {
        quantity: slackReminder?.schedule?.quantity ?? 1,
        unit: normalizeTimeOffsetUnit(slackReminder?.schedule?.unit) ?? TimeOffsetUnit.HOURS,
        relation:
          slackReminder?.schedule?.beforeEventBegins === false
            ? TimeOffsetRelation.AFTER
            : TimeOffsetRelation.BEFORE,
        isRelativeTime: isCreate ? !event.hasEnded : !!slackReminder?.schedule,
        sendAt:
          slackReminder?.datetime && !slackReminder.schedule
            ? convertSendAtToFormValue(slackReminder.datetime, eventTimezone)
            : defaultPostEventSendAt
      }
    }
  });

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange?.(form.formState.isDirty);
  }, [form.formState.isDirty, onDirtyChange]);

  const upsertSlackReminderMutation = useMutation({
    mutationFn: (data: UpsertSlackReminderInput) => {
      return slackRemindersApi.upsert({ input: data });
    },
    onSuccess: (data) => {
      if (data.ok) {
        // Close the form.
        showToast(`Slack reminder ${isCreate ? 'created' : 'updated'} successfully`);
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        onSuccess?.();
        return;
      }
      showToast(`Failed to ${isCreate ? 'create' : 'update'} slack reminder`, {
        variant: ToasterVariant.ERROR
      });
    },
    onError: (error) => {
      console.error(`Failed to ${isCreate ? 'create' : 'update'} slack reminder:`, error);
      showToast(`Failed to ${isCreate ? 'create' : 'update'} slack reminder`, {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const deleteSlackReminderMutation = useMutation({
    mutationFn: (input: DeleteSlackReminderInput) => {
      return slackRemindersApi.delete({ input });
    },
    onSuccess: (data) => {
      if (data.ok) {
        showToast('Slack reminder deleted successfully');
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        onSuccess?.();
        return;
      }
      showToast('Failed to delete slack reminder', {
        variant: ToasterVariant.ERROR
      });
    },
    onError: (error) => {
      console.error('Failed to delete slack reminder:', error);
      showToast('Failed to delete slack reminder', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const onSave = (data: SlackReminderFormValues): void => {
    /**
     * Transform the form data to the expected format for the backend.
     * local schedule.sendAt -> backend 'datetime'
     * local schedule.relation ENUM -> backend 'beforeEventBegins' boolean
     */
    const isRelative = data.schedule.isRelativeTime;

    const transformedData: UpsertSlackReminderInput = {
      eventId: event.id!,
      ...(slackReminder?.id ? { id: slackReminder.id } : {}),
      message: data.message,
      title: data.title,
      selectedGuestStatuses: data.selectedGuestStatuses,
      ...(isRelative
        ? {
            schedule: {
              quantity: data.schedule.quantity!,
              unit: data.schedule.unit!,
              beforeEventBegins: data.schedule.relation === TimeOffsetRelation.BEFORE
            }
          }
        : { datetime: data.schedule.sendAt! })
    };

    upsertSlackReminderMutation.mutate(transformedData);
  };

  const onDelete = (): void => {
    if (!slackReminder?.id) return;
    deleteSlackReminderMutation.mutate({ id: slackReminder.id, eventId: event.id! });
  };

  return (
    <>
      {isCreate ? (
        <SheetHeader className="tw-align-start tw-mb-0 tw-flex-shrink-0 tw-p-6 tw-pb-0 ">
          <button
            type="button"
            data-testid="back-button"
            onClick={onBack}
            className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-text-content-tertiary"
          >
            <Icon name="arrow-left" />
            <span className="tw-text-sm tw-font-normal tw-leading-5">Back</span>
          </button>
          <SheetTitle className="tw-mb-0">Create Slack reminder</SheetTitle>
          <SheetDescription id="create-reminder-sheet" className="tw-sr-only tw-mb-0">
            Create a slack reminder for the event
          </SheetDescription>
        </SheetHeader>
      ) : (
        <SheetHeader className="tw-align-start tw-mb-0 tw-flex-shrink-0 tw-p-6 tw-pb-0 ">
          <SheetTitle className="tw-mb-0">Update Slack reminder</SheetTitle>
        </SheetHeader>
      )}

      <Form {...form}>
        <form
          id="slack-reminder-form"
          onSubmit={form.handleSubmit(onSave, (errors) => {
            console.error(errors);
          })}
          className="tw-flex tw-min-h-0 tw-flex-grow tw-flex-col"
        >
          <div className="tw-flex tw-min-h-0 tw-flex-grow tw-flex-col tw-overflow-y-auto tw-px-6 tw-pb-6">
            <InputField
              name="title"
              label="Title"
              placeholder="Title"
              description="Guests won't see this-it's just for you to organize communications"
            />

            <TextareaField
              name="message"
              label="Message"
              placeholder="Message"
              inputClassName="tw-min-h-[200px]"
              helpText={
                <div>
                  <span className="tw-m-0 tw-p-0 tw-text-xs">See Slack </span>
                  <Button
                    variant="link"
                    className="tw-m-0 tw-h-3 tw-p-0 tw-text-xs"
                    onClick={() => setShowSlackTipsModal(true)}
                  >
                    formatting tips
                  </Button>
                </div>
              }
            />
            {/* Guest Statuses */}
            <MultiSelectField
              name="selectedGuestStatuses"
              label="Send to guests who are:"
              placeholder="Select guest statuses..."
              options={guestStatusOptions}
            />

            {/* Schedule */}
            <ReminderScheduler />
          </div>

          {/* Submission buttons */}
          <div className="tw-flex tw-flex-shrink-0 tw-flex-col">
            <hr className="tw-my-0 tw-w-full tw-border-gray-300 " />
            <SheetFooter className="tw-flex tw-w-full tw-flex-shrink-0 tw-justify-between tw-p-6 ">
              <Button
                type="submit"
                variant="primary"
                className="tw-h-10 tw-rounded-lg tw-p-3"
                loading={upsertSlackReminderMutation.isPending}
                disabled={upsertSlackReminderMutation.isPending}
              >
                {isCreate ? 'Create' : 'Update'}
              </Button>
              {!isCreate && (
                <Button
                  type="button"
                  variant="ghost"
                  className="tw-h-10 tw-rounded-lg tw-bg-transparent tw-p-3 tw-text-negative-foreground"
                  loading={upsertSlackReminderMutation.isPending}
                  disabled={upsertSlackReminderMutation.isPending}
                  onClick={onDelete}
                >
                  <Icon name="trash" className="tw-h-4 tw-w-4 tw-text-negative-foreground" />
                  <span className="tw-ml-1">Delete</span>
                </Button>
              )}
            </SheetFooter>
          </div>
        </form>
      </Form>
      <SlackTipsModal open={showSlackTipsModal} onOpenChange={setShowSlackTipsModal} />
    </>
  );
};
